"use client";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { CheckIcon, ChevronsUpDown } from "lucide-react";
import * as React from "react";
import Swal from 'sweetalert2';

const countries = [
  { code: "PY", name: "Paraguay", flag: "🇵🇾", allowed: true },
  { code: "AR", name: "Argentina", flag: "🇦🇷", allowed: false },
  { code: "BR", name: "Brasil", flag: "🇧🇷", allowed: false },
  { code: "UY", name: "Uruguay", flag: "🇺🇾", allowed: false },
  { code: "CL", name: "Chile", flag: "🇨🇱", allowed: false },
  { code: "BO", name: "Bolivia", flag: "🇧🇴", allowed: false },
  { code: "PE", name: "Perú", flag: "🇵🇪", allowed: false },
  { code: "CO", name: "Colombia", flag: "🇨🇴", allowed: false },
  { code: "VE", name: "Venezuela", flag: "🇻🇪", allowed: false },
  { code: "EC", name: "Ecuador", flag: "🇪🇨", allowed: false },
  { code: "MX", name: "México", flag: "🇲🇽", allowed: false },
  { code: "US", name: "Estados Unidos", flag: "🇺🇸", allowed: false },
  { code: "ES", name: "España", flag: "🇪🇸", allowed: false },
];

const CountrySelector = React.forwardRef(({
  className,
  onChange,
  value,
  ...props
}, ref) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const [selectedCountry, setSelectedCountry] = React.useState(
    countries.find(c => c.code === value) || countries[0]
  );

  const handleSelect = (country) => {
    if (!country.allowed) {
      Swal.fire({
        icon: 'info',
        title: 'Envíos solo a Paraguay',
        html: `
          <p style="margin-bottom: 16px;">Actualmente solo realizamos envíos dentro de Paraguay.</p>
          <p style="margin-bottom: 16px;">Si deseas realizar un pedido a <strong>${country.name}</strong>, por favor contáctanos por WhatsApp para coordinar el envío.</p>
        `,
        showCancelButton: true,
        confirmButtonText: '<i class="fab fa-whatsapp"></i> Contactar por WhatsApp',
        cancelButtonText: 'Cerrar',
        confirmButtonColor: '#25D366',
        cancelButtonColor: '#6b7280',
      }).then((result) => {
        if (result.isConfirmed) {
          window.open('https://wa.me/5545999456009?text=Hola,%20quisiera%20consultar%20sobre%20envíos%20internacionales', '_blank');
        }
      });
      setIsOpen(false);
      return;
    }

    setSelectedCountry(country);
    onChange?.(country.name);
    setIsOpen(false);
  };

  React.useEffect(() => {
    if (value) {
      const country = countries.find(c => c.code === value || c.name === value);
      if (country) {
        setSelectedCountry(country);
      }
    }
  }, [value]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={isOpen}
          className={cn(
            "w-full justify-between h-9 px-3 text-sm",
            "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
            className
          )}
          {...props}
        >
          <span className="flex items-center gap-2">
            <span className="text-xl">{selectedCountry.flag}</span>
            <span>{selectedCountry.name}</span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput
            value={searchValue}
            onValueChange={setSearchValue}
            placeholder="Buscar país..."
          />
          <CommandList>
            <ScrollArea className="h-72">
              <CommandEmpty>No se encontró el país.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  key={countries[0].code}
                  onSelect={() => handleSelect(countries[0])}
                  className="gap-2"
                >
                  <span className="text-xl">{countries[0].flag}</span>
                  <span className="flex-1">{countries[0].name}</span>
                  <span className="text-xs text-green-600 font-semibold">✓ Envío disponible</span>
                  <CheckIcon
                    className={cn(
                      "ml-auto h-4 w-4",
                      selectedCountry.code === countries[0].code ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
                
                {/* Separator */}
                <div className="px-2 py-1.5">
                  <div className="h-px bg-border" />
                  <p className="text-xs text-muted-foreground mt-1.5">Otros países (contactar por WhatsApp)</p>
                </div>

                {countries.slice(1).map((country) => (
                  <CommandItem
                    key={country.code}
                    onSelect={() => handleSelect(country)}
                    className="gap-2"
                  >
                    <span className="text-xl">{country.flag}</span>
                    <span className="flex-1">{country.name}</span>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded flex items-center gap-1">
                      <i className="fab fa-whatsapp"></i>
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
});

CountrySelector.displayName = "CountrySelector";

export default CountrySelector;
